import { useMutation, useApolloClient } from "@apollo/client";
import { PencilIcon, TrashIcon, PhotoIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

import { DELETE_LIST, COPY_LIST } from "../../../graphql/mutation/List";
import { FETCH_LISTS } from "../../../graphql/query/List";
import {
  LIST_TYPES,
  isImageListType,
  getImageListType,
  getEntityListType,
  canCopyListType,
  isWorkingListType,
} from "../../../constants/listTypes";
import { Button, IconButton, Modal } from "../../ui";
import CopyListModalBody from "./CopyListModalBody";

const TableActions = props => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const {
    listKey = "listId",
    preserveQueryKeys = [],
    statusKey = "selectListWorking",
    onSuccess,
    values,
    typeId,
  } = props || {};
  const { id, name, typeId: listTypeId } = values || {};
  const finalTypeId = typeId || listTypeId;
  const canCopy = finalTypeId ? canCopyListType(finalTypeId) : false;
  const isSourceImageList = finalTypeId ? isImageListType(finalTypeId) : false;
  const targetTypeId = finalTypeId
    ? (isSourceImageList ? getEntityListType(finalTypeId) : getImageListType(finalTypeId))
    : null;
  
  const [deleteList, { loading: deleteLoading }] = useMutation(DELETE_LIST, {
    awaitRefetchQueries: true,
    onCompleted: async () => {
      // Refetch all FetchLists queries regardless of variables to ensure cache is cleared
      await client.refetchQueries({
        include: ["FetchLists"],
      });
    },
  });

  // Build refetch queries based on source and target types
  const refetchQueriesList = [];
  if (finalTypeId) {
    refetchQueriesList.push({
      query: FETCH_LISTS(),
      variables: { input: { typeId: finalTypeId } },
      fetchPolicy: "network-only",
    });
  }
  if (targetTypeId) {
    refetchQueriesList.push({
      query: FETCH_LISTS(),
      variables: { input: { typeId: targetTypeId } },
      fetchPolicy: "network-only",
    });
  }

  const [copyList, { loading: copyLoading }] = useMutation(COPY_LIST({}), {
    refetchQueries: refetchQueriesList,
    awaitRefetchQueries: true,
    onCompleted: async () => {
      // Refetch all FetchLists queries regardless of variables to ensure cache is cleared
      await client.refetchQueries({
        include: ["FetchLists"],
      });
      onSuccess?.();
    },
  });

  const handleDelete = closeModal => async () => {
    const res = await deleteList({ variables: { input: { ids: [id] } } });
    if (res?.data?.deleteList?.success) {
      onSuccess?.();
      closeModal();
    }
  };

  const handleCopy = closeModal => async (e) => {
    e?.preventDefault?.();
    const res = await copyList({
      variables: {
        input: {
          listId: id,
          ...(targetTypeId ? { targetTypeId } : {}),
        },
      },
    });
    
    if (res?.data?.copyList?.success) {
      const newList = res?.data?.copyList?.list;
      const newListId = newList?.id;
      const newListTypeId = newList?.typeId;
      closeModal();
      
      // Navigate to the newly created list
      if (newListId && newListTypeId) {
        const isNewListImageList = isImageListType(newListTypeId);
        const isNewListSaved = !isWorkingListType(newListTypeId);
        
        // Determine navigation URL based on list type and module
        let baseUrl = "/instance"; // Default to instance
        let statusKey = "selectListWorking";
        let listIdParam = "listId";
        
        if (newListTypeId === LIST_TYPES.ARTWORK || newListTypeId === LIST_TYPES.ARTWORK_WORKING) {
          baseUrl = "/artworks";
        } else if (newListTypeId === LIST_TYPES.ARTWORK_IMAGE || newListTypeId === LIST_TYPES.ARTWORK_IMAGE_WORKING) {
          baseUrl = "/artworks";
          statusKey = "imageSelectListWorking";
          listIdParam = "imageListId";
        } else if (isNewListImageList) {
          statusKey = "imageSelectListWorking";
          listIdParam = "imageListId";
        }
        
        if (isNewListSaved) {
          navigate(`${baseUrl}?status=${statusKey}&${listIdParam}=${newListId}`);
        } else {
          const workingListParam = isNewListImageList ? "imageWorkingListId" : "workingListId";
          navigate(`${baseUrl}?status=${statusKey}&${workingListParam}=${newListId}`);
        }
      }
    } else if (res?.data?.copyList?.error) {
      console.error("Error copying list:", res.data.copyList.error);
      // You might want to show an error message to the user here
    }
  };

  const handleEdit = () => {
    const currentParams = new URLSearchParams(window.location.search);
    const nextParams = new URLSearchParams({
      status: statusKey,
      [listKey]: id,
    });

    preserveQueryKeys.forEach(key => {
      if (!key || key === "status" || key === listKey) {
        return;
      }

      const value = currentParams.get(key);
      if (value) {
        nextParams.set(key, value);
      }
    });

    navigate(`?${nextParams.toString()}`);
  };

  const deleteModalProps = {
    title: "",
    scale: "md",
    hideCloseButton: true,
    closeOnBackdrop: false,
    body: ({ closeModal }) => {
      return (
        <div className={`p-4`}>
          <div className={`flex flex-row items-center justify-between`}>
            <div className={`text-lg`}>Confirmation</div>
            <div className={`flex gap-6`}>
              <Button
                className={`!w-28`}
                action="default"
                label="Cancel"
                onClick={closeModal}
              />
              <Button
                loading={deleteLoading}
                className={`flex !w-28 items-center`}
                label={deleteLoading ? "Deleting" : "Delete list"}
                onClick={handleDelete(closeModal)}
              />
            </div>
          </div>
          <div className="mt-8 text-sm font-normal">
            {`You are about to delete "${name}". Are you sure?`}
          </div>
        </div>
      );
    },
  };

  const copyModalProps = {
    title: "",
    scale: "md",
    hideCloseButton: true,
    closeOnBackdrop: false,
    body: ({ closeModal }) => {
      return (
        <CopyListModalBody
          loading={copyLoading}
          listName={name}
          onCopy={handleCopy(closeModal)}
          onCancel={closeModal}
          isImageToEntity={isSourceImageList}
        />
      );
    },
  };

  if (typeof id == "number") {
    return (
      <div className="user-list-actions flex text-right text-sm font-medium">
        {id && (
          <div className="flex flex-row gap-4">
            <IconButton variant="clean" title="Edit Select List">
              <PencilIcon
                className={`h-4 w-4 cursor-pointer text-gray-400`}
                onClick={handleEdit}
              />
            </IconButton>
            {canCopy && (
              <Modal {...copyModalProps}>
                <IconButton
                  variant="clean"
                  title={isSourceImageList ? "Copy to Select List" : "Copy to Image Select List"}
                >
                  <PhotoIcon className={`h-4 w-4 cursor-pointer text-gray-400`} />
                </IconButton>
              </Modal>
            )}
            <Modal {...deleteModalProps}>
              <IconButton variant="clean" title="Delete Select List">
                <TrashIcon className={`h-4 w-4 cursor-pointer text-gray-400`} />
              </IconButton>
            </Modal>
          </div>
        )}
      </div>
    );
  } else {
    return false;
  }
};

export default TableActions;
